/** Iter 114/116 — public quote approval page (/quote/:token). Customer reviews
 * the priced quote and approves it, converting it into a confirmed
 * reservation. Bilingual EN/Shqip: default from quote.lang (server infers
 * Albanian for +355 phones / .al emails), switchable via the toggle. */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Money from "@/components/Money";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, Clock, XCircle, CalendarDays, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const T = {
  en: {
    kicker: "Rental quote from",
    day: "day", days: "days",
    vehicleRental: "Vehicle rental",
    included: "(included)",
    insurance: "Insurance",
    deliveryFee: "Delivery / one-way fee",
    meetGreet: "Meet & Greet",
    discount: "Discount",
    vat: "VAT",
    grandTotal: "Grand Total",
    depositCharged: "Security deposit (charged)",
    depositRefundable: "Security deposit (refundable, at pickup)",
    validUntil: "Valid until",
    yourDetails: "Your details",
    fullName: "Full name",
    email: "Email",
    phone: "Phone / WhatsApp (optional)",
    approve: "Approve quote",
    approveNote: (biz) => `Approving creates a confirmed reservation — payment is collected by ${biz}.`,
    confirmed: "Reservation confirmed!",
    reference: "Reference",
    total: "Total",
    balanceDue: "Balance due at pickup",
    emailOnWay: (e) => `A confirmation email is on its way to ${e}.`,
    stateApproved: "This quote was already approved — the reservation is confirmed.",
    stateExpired: "This quote has expired. Please contact the rental company for a new one.",
    stateCancelled: "This quote was cancelled by the rental company.",
    errName: "Please enter your name and a valid email",
    errApprove: "Could not approve the quote",
  },
  sq: {
    kicker: "Ofertë qiraje nga",
    day: "ditë", days: "ditë",
    vehicleRental: "Qiraja e automjetit",
    included: "(e përfshirë)",
    insurance: "Sigurimi",
    deliveryFee: "Tarifa e dorëzimit / një drejtim",
    meetGreet: "Pritje në aeroport",
    discount: "Zbritje",
    vat: "TVSH",
    grandTotal: "Totali",
    depositCharged: "Depozita e sigurisë (e faturuar)",
    depositRefundable: "Depozita e sigurisë (e rikthyeshme, në marrje)",
    validUntil: "E vlefshme deri më",
    yourDetails: "Të dhënat tuaja",
    fullName: "Emri i plotë",
    email: "Email",
    phone: "Telefon / WhatsApp (opsionale)",
    approve: "Aprovo ofertën",
    approveNote: (biz) => `Aprovimi krijon një rezervim të konfirmuar — pagesa mblidhet nga ${biz}.`,
    confirmed: "Rezervimi u konfirmua!",
    reference: "Referenca",
    total: "Totali",
    balanceDue: "Balanca për t'u paguar në marrje",
    emailOnWay: (e) => `Një email konfirmimi po dërgohet te ${e}.`,
    stateApproved: "Kjo ofertë është aprovuar tashmë — rezervimi është i konfirmuar.",
    stateExpired: "Kjo ofertë ka skaduar. Ju lutemi kontaktoni kompaninë e qirasë për një të re.",
    stateCancelled: "Kjo ofertë u anulua nga kompania e qirasë.",
    errName: "Ju lutemi shkruani emrin dhe një email të vlefshëm",
    errApprove: "Oferta nuk mund të aprovohej",
  },
};

function Row({ label, value, strong, tone }) {
  return (
    <div className={`flex items-center justify-between gap-2 py-0.5 ${strong ? "font-bold" : ""} ${tone || ""}`}>
      <span>{label}</span>
      <span className="tabular-nums shrink-0"><Money amount={value} /></span>
    </div>
  );
}

export default function QuoteApprovalPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [approved, setApproved] = useState(null);
  const [lang, setLang] = useState("en");
  const t = T[lang] || T.en;

  useEffect(() => {
    axios.get(`${API}/quotes/public/${token}`)
      .then((r) => {
        setData(r.data);
        const q = r.data.quote || {};
        setLang(q.lang === "sq" ? "sq" : "en");
        setForm({ name: q.customer_name || "", email: q.customer_email || "", phone: q.customer_phone || "" });
      })
      .catch((e) => setErr(e?.response?.data?.detail || "Quote not found"));
  }, [token]);

  const approve = async () => {
    if (!form.name.trim() || !form.email.includes("@")) { toast.error(t.errName); return; }
    setBusy(true);
    try {
      const { data: r } = await axios.post(`${API}/quotes/public/${token}/approve`, {
        customer_name: form.name.trim(), customer_email: form.email.trim(), customer_phone: form.phone.trim() || null,
      });
      setApproved(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t.errApprove);
    } finally { setBusy(false); }
  };

  if (err) return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="text-center" data-testid="quote-error"><XCircle className="h-10 w-10 text-rose-500 mx-auto mb-3" /><p className="text-lg font-semibold">{err}</p></div>
    </div>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-neutral-400" /></div>;

  const { quote: q, car, vendor, state } = data;
  const pr = q.pricing || {};
  const img = car.image_url || (car.images || [])[0];

  const LangToggle = () => (
    <div className="flex gap-1" data-testid="quote-lang-toggle">
      {["sq", "en"].map((l) => (
        <button key={l} type="button" onClick={() => setLang(l)}
          className={`text-[11px] font-bold uppercase rounded-full px-2 py-0.5 border ${lang === l ? "bg-neutral-900 text-white border-neutral-900" : "border-border text-neutral-500"}`}
          data-testid={`quote-lang-${l}`}>
          {l === "sq" ? "🇦🇱 Shqip" : "🇬🇧 EN"}
        </button>
      ))}
    </div>
  );

  if (approved) return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-neutral-50">
      <div className="max-w-md w-full bg-white rounded-2xl border border-border p-8 text-center" data-testid="quote-approved-state">
        <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
        <h1 className="text-2xl font-bold mb-2">{t.confirmed}</h1>
        <p className="text-neutral-600 mb-1">{t.reference} <span className="font-mono font-bold">#{approved.reference}</span></p>
        <p className="text-neutral-600">{t.total} <strong>€{approved.grand_total?.toFixed(2)}</strong> · {t.balanceDue} €{approved.balance_due?.toFixed(2)}</p>
        <p className="text-sm text-neutral-500 mt-3">{t.emailOnWay(form.email)}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-neutral-50 py-8 px-4">
      <div className="max-w-lg mx-auto bg-white rounded-2xl border border-border overflow-hidden" data-testid="quote-page">
        {img && <img src={img} alt={`${car.make} ${car.model}`} className="w-full h-48 object-cover" />}
        <div className="p-6 space-y-4">
          <div>
            <div className="flex items-start justify-between gap-2">
              <div className="text-xs uppercase tracking-wider text-blue-600 font-bold">{t.kicker} {vendor.business_name}</div>
              <LangToggle />
            </div>
            <h1 className="text-2xl font-bold mt-1">{car.make} {car.model}{car.year ? ` · ${car.year}` : ""}</h1>
            <div className="text-sm text-neutral-500 mt-1 flex flex-wrap gap-x-4 gap-y-1">
              <span className="inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" />{(q.start_date || "").slice(0, 10)} → {(q.end_date || "").slice(0, 10)}</span>
              {q.pickup_location && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{q.pickup_location}</span>}
            </div>
          </div>

          {state !== "sent" && (
            <div className={`rounded-xl px-3 py-2 text-sm font-semibold ${state === "approved" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`} data-testid="quote-state-banner">
              {state === "approved" ? t.stateApproved : state === "expired" ? t.stateExpired : t.stateCancelled}
            </div>
          )}

          <div className="rounded-xl border border-border p-3 text-sm" data-testid="quote-breakdown">
            <Row label={`${t.vehicleRental} · €${(pr.daily_rate || 0).toFixed(2)} × ${pr.days || 0} ${pr.days === 1 ? t.day : t.days}`} value={pr.rental_subtotal || 0} />
            {(pr.add_ons || []).map((a, i) => (
              <Row key={i} label={`${a.name}${(a.qty || 1) > 1 ? ` ×${a.qty}` : ""}${a.free ? ` ${t.included}` : ""}`} value={a.fee || 0} />
            ))}
            {pr.insurance_fee > 0 && <Row label={`${t.insurance}${q.insurance_plan_name ? ` · ${q.insurance_plan_name}` : ""}`} value={pr.insurance_fee} />}
            {pr.route_fee > 0 && <Row label={t.deliveryFee} value={pr.route_fee} />}
            {pr.meet_greet_fee > 0 && <Row label={t.meetGreet} value={pr.meet_greet_fee} />}
            {(pr.custom_charges || []).map((c, i) => <Row key={`c${i}`} label={c.label} value={c.amount} />)}
            {pr.manual_discount?.amount > 0 && <Row label={t.discount} value={-pr.manual_discount.amount} tone="text-emerald-700" />}
            {pr.vat_amount > 0 && <Row label={`${t.vat} ${pr.vat_rate_pct || 0}%`} value={pr.vat_amount} />}
            <div className="border-t border-border mt-1.5 pt-1.5">
              <Row label={t.grandTotal} value={pr.grand_total || 0} strong />
              {pr.deposit > 0 && <Row label={pr.deposit_in_total ? t.depositCharged : t.depositRefundable} value={pr.deposit} tone="text-neutral-500 text-xs" />}
            </div>
          </div>

          {q.notes && <p className="text-sm text-neutral-600 bg-neutral-50 rounded-lg p-3">{q.notes}</p>}
          <p className="text-xs text-neutral-400 inline-flex items-center gap-1"><Clock className="h-3 w-3" />{t.validUntil} {(q.expires_at || "").slice(0, 10)}</p>

          {state === "sent" && (
            <div className="space-y-2 border-t border-border pt-4">
              <div className="text-sm font-bold">{t.yourDetails}</div>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder={t.fullName} data-testid="quote-name" />
              <Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder={t.email} data-testid="quote-email" />
              <Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder={t.phone} data-testid="quote-phone" />
              <Button onClick={approve} disabled={busy} className="w-full rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-11" data-testid="quote-approve-btn">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                {t.approve} · <Money amount={pr.grand_total || 0} className="ml-1" />
              </Button>
              <p className="text-[11px] text-neutral-400 text-center">{t.approveNote(vendor.business_name)}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
